import { HttpClient } from '@wix/http-client';
import type {
  AppDataComponent,
  DocumentServicesObject,
} from '@wix/document-services-types';
import { CompRef } from '@wix/document-services-types';
import type { PluginInstallationParams } from '@wix/editor-platform-host-integration-apis';
import {
  ComponentType,
  PluginInterface,
  WidgetPluginComponentData,
} from '@wix/ambassador-app-service-webapp/types';
import { SlotData, WidgetPointer } from '../../types/widgetPlugins';

interface SlotsResponse {
  slots: SlotData[];
}

const getVirtualSlotsPlaceholderCompRef = (
  widgetCompRef: CompRef,
  slotName: string,
): CompRef => {
  const VIRTUAL_SLOT_ID_DIVIDER = '_vs_';

  return {
    id: `${widgetCompRef.id}${VIRTUAL_SLOT_ID_DIVIDER}${slotName}`,
    type: widgetCompRef.type,
  };
};

const getSlotComponentRef = (
  documentServicesAPI: DocumentServicesObject,
  slot: SlotData,
): CompRef | null => {
  const slotComponentRef = documentServicesAPI.components.get.byId(
    slot.componentId,
  );
  return (
    slotComponentRef &&
    getVirtualSlotsPlaceholderCompRef(slotComponentRef, slot.slotRole)
  );
};

const getAvailableSlots = async (
  documentServicesAPI: DocumentServicesObject,
  pluginInterfaces?: PluginInterface[],
): Promise<SlotData[]> => {
  const META_SITE_APPLICATION_ID = '-666';
  const getAppToken = () =>
    documentServicesAPI.platform.getAppDataByApplicationId(
      META_SITE_APPLICATION_ID,
    )?.instance;

  const httpClient = new HttpClient({
    baseURL: '/_serverless/editor-platform-widget-plugins/',
    getAppToken,
  });

  const res = await httpClient.get<SlotsResponse>('/slots');

  // TODO: We should implement this filtration on the server side
  return pluginInterfaces && pluginInterfaces.length
    ? res.data.slots.filter((slot) => {
        const slotInterfaces = new Set(slot.interfaces);
        return pluginInterfaces.every((item) => slotInterfaces.has(item));
      })
    : res.data.slots;
};

const getPluginInstallationParams = async (
  documentServicesAPI: DocumentServicesObject,
  appDefinitionId: string,
  components: AppDataComponent[],
): Promise<PluginInstallationParams | null> => {
  const isPluginCompType = (comp: AppDataComponent) =>
    comp.type === ComponentType.WIDGET_PLUGIN;

  // WidgetPluginComponentData has an explicit type as AppDataComponent['data'] doesn't provide a correct type for all possible component types
  // @ts-expect-error
  const widgetPlugins: WidgetPluginComponentData[] =
    components?.filter(isPluginCompType).map((w) => w.data) ?? [];

  if (widgetPlugins?.length === 0) {
    return null;
  }

  for (const plugin of widgetPlugins) {
    const availableSlots = await getAvailableSlots(
      documentServicesAPI,
      plugin.pluginInterfaces,
    );

    if (!availableSlots.length) {
      continue;
    }

    const [slot] = availableSlots;
    const slotComponentRef = getSlotComponentRef(documentServicesAPI, slot);

    if (!slotComponentRef) {
      return null;
    }

    const widgetRef: WidgetPointer = {
      appDefinitionId,
      widgetId: plugin.referenceComponentId!,
    };

    return {
      slotComponentRef,
      widgetRef,
      pageId: slot.pageId,
    };
  }

  return null;
};

export { getPluginInstallationParams };
